/**
 * Layer Selection Modal
 * Modal for selecting parking layers from DXF file analysis
 */

import { useState, useEffect, useRef } from 'react'
import type { RecoverySelection } from 'components/parking-agent/recoveryCsv'
import { LayerInfo, SampleShape } from '../../../services/DxfParserService'
import { LayerMappingPresetService } from '../../../services/LayerMappingPresetService'
import type { LayerMappingPreset } from '../../../models/LayerMappingPreset'
import {
  DXF_STANDARD_TYPE_OPTIONS,
  applyBulkTypeToSelection,
  buildConvertSelectedLayers,
  effectiveSuggestedType,
  getTypeDisplayName,
} from '../lib/layerSelectionTypes'
import styles from './LayerSelectionModal.module.css'
import ParkingRecoveryPanel from 'components/parking-agent/ParkingRecoveryPanel'
import { selectBasicObjectLayers } from '../lib/basicObjectExtraction'
import BasicObjectSummary from './BasicObjectSummary'
import { useCsvRuntimeStore } from '../model/csvStore'

interface SelectedLayerInfo {
  name: string
  suggestedType: string
}

interface ShapePreviewProps {
  shape: SampleShape | undefined
  type: string
}

function ShapePreview({ shape, type }: ShapePreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const size = 30
    canvas.width = size
    canvas.height = size

    // Clear canvas
    ctx.clearRect(0, 0, size, size)

    // Set color based on type
    const getColor = (t: string) => {
      if (t.startsWith('p-parking-disable')) return '#e11d48'
      if (t.startsWith('p-parking-electric')) return '#10b981'
      if (t.startsWith('p-parking-women')) return '#ec4899'
      if (t.startsWith('p-parking-large')) return '#f59e0b'
      if (t.startsWith('p-parking-small')) return '#8b5cf6'
      if (t.startsWith('p-parking-delivery')) return '#6366f1'
      if (t.startsWith('p-parking')) return '#3b82f6'
      if (
        t.startsWith('p-cctv') ||
        t.startsWith('p-emergency') ||
        t.startsWith('p-sensor') ||
        t.startsWith('p-speaker')
      ) {
        return '#0ea5e9'
      }
      if (t.startsWith('l-')) return '#eab308'
      if (t.startsWith('marker')) return '#fbbf24'
      return '#6b7280'
    }

    if (!shape || !shape.coordinates || shape.coordinates.length === 0) {
      // Draw placeholder if no shape data
      ctx.fillStyle = getColor(type) + '30' // 30% opacity
      ctx.fillRect(5, 5, 20, 20)
      ctx.strokeStyle = getColor(type)
      ctx.lineWidth = 1
      ctx.strokeRect(5, 5, 20, 20)
      return
    }

    // Draw the shape
    ctx.beginPath()

    // Shape coordinates are already normalized to 0-100 range
    const scale = (size / 100) * 0.8 // 80% of canvas size
    const offset = size * 0.1 // 10% padding

    shape.coordinates.forEach((point, index) => {
      const x = point[0] * scale + offset
      const y = point[1] * scale + offset

      if (index === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    })

    ctx.closePath()

    // Fill and stroke
    ctx.fillStyle = getColor(type) + '40' // 40% opacity
    ctx.fill()
    ctx.strokeStyle = getColor(type)
    ctx.lineWidth = 1.5
    ctx.stroke()
  }, [shape, type])

  return <canvas ref={canvasRef} className={styles.shapePreview} />
}

interface LayerSelectionModalProps {
  layers: LayerInfo[]
  isOpen: boolean
  onConfirm: (selectedLayers: SelectedLayerInfo[], recovery?: RecoverySelection) => void
  onCancel: () => void
  filename: string
  sourceFilename?: string
  /** 저장된 레이어 매핑 프리셋 불러오기/저장 — 없으면 이 기능을 숨긴다. */
  siteProjectId?: number | null
}

export function LayerSelectionModal({
  layers,
  isOpen,
  onConfirm,
  onCancel,
  filename,
  sourceFilename,
  siteProjectId,
}: LayerSelectionModalProps) {
  const [selectedLayers, setSelectedLayers] = useState<Set<string>>(new Set())
  const [typeOverrides, setTypeOverrides] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState<'layers' | 'recovery'>('layers')
  const [recoveryOpened, setRecoveryOpened] = useState(false)
  const [recovery, setRecovery] = useState<RecoverySelection | null>(null)
  const recoveryCount = recovery?.candidates.length || 0
  const [bulkType, setBulkType] = useState<string>('p-parking-basic')

  // 저장된 레이어 매핑 프리셋 — 불러오기/저장.
  const [presets, setPresets] = useState<LayerMappingPreset[]>([])
  const [presetsLoading, setPresetsLoading] = useState(false)
  const [selectedPresetId, setSelectedPresetId] = useState<string>('')
  const [presetName, setPresetName] = useState<string>('')
  const [presetSaving, setPresetSaving] = useState(false)
  const [presetMessage, setPresetMessage] = useState<string | null>(null)

  const excludeIsolated = useCsvRuntimeStore((state) => state.excludeIsolatedOnImport)
  const setExcludeIsolated = useCsvRuntimeStore((state) => state.setExcludeIsolatedOnImport)
  const selectOptions = DXF_STANDARD_TYPE_OPTIONS

  // Auto-select layers based on suggested type; reset overrides when analysis list changes
  useEffect(() => {
    if (isOpen) {
      setRecovery(null)
      setRecoveryOpened(false)
      setActiveTab('layers')
      setSelectedLayers(selectBasicObjectLayers(layers))
      setTypeOverrides({})
      setPresetName('')
      setPresetMessage(null)
      setSelectedPresetId('')
    }
  }, [layers, isOpen, sourceFilename, filename, siteProjectId])

  // 모달이 열릴 때 이 현장에 저장된 프리셋 목록을 불러온다.
  useEffect(() => {
    if (!isOpen || siteProjectId == null) return
    setPresetsLoading(true)
    LayerMappingPresetService.list(siteProjectId)
      .then(setPresets)
      .catch(() => setPresets([]))
      .finally(() => setPresetsLoading(false))
  }, [isOpen, siteProjectId])

  if (!isOpen) return null

  const handleLoadPreset = () => {
    const preset = presets.find((p) => String(p.id) === selectedPresetId)
    if (!preset) return

    const analyzedNames = new Set(layers.map((l) => l.name))
    const matched = preset.mappings.filter((m) => analyzedNames.has(m.layer_name))

    setSelectedLayers((prev) => {
      const next = new Set(prev)
      matched.forEach((m) => next.add(m.layer_name))
      return next
    })
    setTypeOverrides((prev) => {
      const next = { ...prev }
      matched.forEach((m) => {
        next[m.layer_name] = m.suggested_type
      })
      return next
    })
    setPresetMessage(
      `"${preset.name}" 적용됨 — 레이어 이름이 일치하는 ${matched.length}/${preset.mappings.length}개에 반영됐습니다.`
    )
  }

  const handleSavePreset = async () => {
    if (siteProjectId == null || !presetName.trim() || selectedLayers.size === 0) return
    setPresetSaving(true)
    setPresetMessage(null)
    try {
      const mappings = Array.from(selectedLayers).map((name) => ({
        layer_name: name,
        suggested_type: getEffectiveType(name),
      }))
      const created = await LayerMappingPresetService.create(siteProjectId, presetName.trim(), mappings)
      setPresets((prev) => [created, ...prev])
      setPresetName('')
      setPresetMessage(`"${created.name}" 저장됐습니다 — 같은 현장의 다른 사용자도 불러올 수 있어요.`)
    } catch (e) {
      setPresetMessage(e instanceof Error ? e.message : '프리셋 저장에 실패했습니다.')
    } finally {
      setPresetSaving(false)
    }
  }

  const getEffectiveType = (layerName: string) => effectiveSuggestedType(layerName, layers, typeOverrides)

  const handleToggleLayer = (layerName: string) => {
    const newSelected = new Set(selectedLayers)
    if (newSelected.has(layerName)) {
      newSelected.delete(layerName)
    } else {
      newSelected.add(layerName)
    }
    setSelectedLayers(newSelected)
  }

  const handleSelectAll = () => {
    setSelectedLayers(new Set(layers.map((l) => l.name)))
  }

  const handleDeselectAll = () => {
    setSelectedLayers(new Set())
  }

  const handleBulkApply = () => {
    if (selectedLayers.size === 0) return
    setTypeOverrides((prev) => applyBulkTypeToSelection(prev, selectedLayers, bulkType))
  }

  const handleRowTypeChange = (layerName: string, value: string) => {
    setTypeOverrides((prev) => ({ ...prev, [layerName]: value }))
  }

  const handleConfirm = () => {
    const selectedLayerInfo = buildConvertSelectedLayers(Array.from(selectedLayers), layers, typeOverrides)
    onConfirm(selectedLayerInfo, recovery || undefined)
  }

  const getTotalEntityCount = () => {
    return Array.from(selectedLayers).reduce((total, layerName) => {
      const layer = layers.find((l) => l.name === layerName)
      return total + (layer?.count || 0)
    }, 0)
  }

  const getTypeColor = (type: string) => {
    if (type.startsWith('p-parking-disable')) return '#e11d48'
    if (type.startsWith('p-parking-electric')) return '#10b981'
    if (type.startsWith('p-parking-women')) return '#ec4899'
    if (type.startsWith('p-parking-large')) return '#f59e0b'
    if (type.startsWith('p-parking-small')) return '#8b5cf6'
    if (type.startsWith('p-parking-delivery')) return '#6366f1'
    if (type.startsWith('p-parking')) return '#3b82f6'
    if (
      type.startsWith('p-cctv') ||
      type.startsWith('p-emergency') ||
      type.startsWith('p-sensor') ||
      type.startsWith('p-speaker')
    ) {
      return '#0ea5e9'
    }
    if (type.startsWith('l-')) return '#eab308'
    if (type.startsWith('marker')) return '#fbbf24'
    return '#6b7280'
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="dxf-extraction-title">
        <div className={styles.body}>
          <div className={styles.header}>
            <h2 id="dxf-extraction-title" className={styles.title}>
              DXF 객체 추출
            </h2>
            <p className={styles.subtitle}>
              DXF 파일: <strong>{filename}</strong>
            </p>
            <p className={styles.description}>
              주차구역·비상벨·CCTV·기둥 후보를 기본 선택합니다. 조명 등 다른 레이어와 복원 주차면을 선택한 뒤 한 번에
              가져오세요.
            </p>
            <label className={styles.isolationOption}>
              <input
                type="checkbox"
                checked={excludeIsolated}
                onChange={(event) => setExcludeIsolated(event.target.checked)}
              />
              본 도면에서 멀리 떨어진 소규모 객체 묶음 제외
            </label>
            <p className={styles.description}>
              별도의 큰 주차구역은 유지합니다. 제외된 객체도 필요하면 이 옵션을 끄고 다시 변환하세요.
            </p>
            <BasicObjectSummary
              types={[
                ...Array.from(selectedLayers).map((name) => getEffectiveType(name)),
                ...(recoveryCount ? ['p-parking-basic'] : []),
              ]}
              candidates
            />
          </div>

          {siteProjectId != null && (
            <div className={styles.tabs} role="tablist" aria-label="가져올 도면 객체 선택">
              {(['layers', 'recovery'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  id={`dxf-${tab}-tab`}
                  aria-controls={`dxf-${tab}-panel`}
                  aria-selected={activeTab === tab}
                  tabIndex={activeTab === tab ? 0 : -1}
                  className={styles.tab}
                  onClick={() => {
                    setActiveTab(tab)
                    if (tab === 'recovery') setRecoveryOpened(true)
                  }}
                  onKeyDown={(event) => {
                    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
                    event.preventDefault()
                    const next =
                      event.key === 'Home'
                        ? 'layers'
                        : event.key === 'End'
                          ? 'recovery'
                          : tab === 'layers'
                            ? 'recovery'
                            : 'layers'
                    setActiveTab(next)
                    if (next === 'recovery') setRecoveryOpened(true)
                    document.getElementById(`dxf-${next}-tab`)?.focus()
                  }}
                >
                  {tab === 'layers' ? `레이어 선택 (${selectedLayers.size})` : `주차면 복원 후보 (${recoveryCount})`}
                </button>
              ))}
            </div>
          )}
          <div id="dxf-layers-panel" role="tabpanel" aria-labelledby="dxf-layers-tab" hidden={activeTab !== 'layers'}>
            {siteProjectId != null && (
              <div className={styles.bulkMapping}>
                <label className={styles.bulkLabel} htmlFor="layer-preset-load">
                  저장된 레이어 매핑 불러오기 (같은 현장에서 다른 사용자가 저장한 매핑)
                </label>
                <div className={styles.bulkRow}>
                  <select
                    id="layer-preset-load"
                    className={styles.typeSelect}
                    value={selectedPresetId}
                    onChange={(e) => setSelectedPresetId(e.target.value)}
                    disabled={presetsLoading || presets.length === 0}
                  >
                    <option value="">
                      {presetsLoading
                        ? '불러오는 중...'
                        : presets.length === 0
                          ? '저장된 매핑 없음'
                          : '프리셋을 선택하세요'}
                    </option>
                    {presets.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.name} · {p.created_by_name || '알 수 없음'} 저장 · 레이어 {p.mappings.length}개
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={styles.bulkApplyButton}
                    onClick={handleLoadPreset}
                    disabled={!selectedPresetId}
                  >
                    불러오기
                  </button>
                </div>
                {presetMessage && <p className={styles.presetMessage}>{presetMessage}</p>}
              </div>
            )}

            <div className={styles.controls}>
              <div className={styles.selectionInfo}>
                {selectedLayers.size}개 / {layers.length}개 선택됨
                {selectedLayers.size > 0 && (
                  <span className={styles.entityCount}> · 총 {getTotalEntityCount().toLocaleString()}개 엔티티</span>
                )}
              </div>
              <div className={styles.buttons}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLayers(selectBasicObjectLayers(layers))
                    setTypeOverrides({})
                  }}
                  className={styles.selectAllButton}
                >
                  기본 객체 선택
                </button>
                <button type="button" onClick={handleSelectAll} className={styles.selectAllButton}>
                  전체 선택
                </button>
                <button type="button" onClick={handleDeselectAll} className={styles.deselectAllButton}>
                  전체 해제
                </button>
              </div>
            </div>

            <div className={styles.bulkMapping}>
              <label className={styles.bulkLabel} htmlFor="layer-bulk-type">
                선택한 레이어에 타입 일괄 적용
              </label>
              <div className={styles.bulkRow}>
                <select
                  id="layer-bulk-type"
                  className={styles.typeSelect}
                  value={bulkType}
                  onChange={(e) => setBulkType(e.target.value)}
                >
                  {selectOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.labelKo} ({opt.value})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={styles.bulkApplyButton}
                  onClick={handleBulkApply}
                  disabled={selectedLayers.size === 0}
                >
                  적용
                </button>
              </div>
              {siteProjectId != null && (
                <div className={styles.groupMemoRow}>
                  <label className={styles.groupMemoLabel} htmlFor="layer-preset-name">
                    이 매핑을 프리셋으로 저장 (선택 — 같은 현장의 다른 사용자도 불러올 수 있어요)
                  </label>
                  <div className={styles.bulkRow}>
                    <input
                      id="layer-preset-name"
                      type="text"
                      className={styles.groupMemoInput}
                      placeholder="예: 표준 주차장 도면"
                      value={presetName}
                      onChange={(e) => setPresetName(e.target.value)}
                    />
                    <button
                      type="button"
                      className={styles.bulkApplyButton}
                      onClick={() => void handleSavePreset()}
                      disabled={!presetName.trim() || selectedLayers.size === 0 || presetSaving}
                    >
                      {presetSaving ? '저장 중...' : '저장'}
                    </button>
                  </div>
                  {presetMessage && <p className={styles.presetMessage}>{presetMessage}</p>}
                </div>
              )}
            </div>

            <div className={styles.layerList}>
              {layers.map((layer) => {
                const effective = getEffectiveType(layer.name)
                const analysis = layer.suggestedType
                const showOverrideHint = effective !== analysis
                return (
                  <div
                    key={layer.name}
                    className={`${styles.layerItem} ${selectedLayers.has(layer.name) ? styles.selected : ''}`}
                    onClick={() => handleToggleLayer(layer.name)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedLayers.has(layer.name)}
                      aria-label={`${layer.name} 레이어 선택`}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => handleToggleLayer(layer.name)}
                      className={styles.checkbox}
                    />

                    <div className={styles.layerInfo}>
                      <ShapePreview shape={layer.sampleShape} type={effective} />
                      <div className={styles.layerDetails}>
                        <div className={styles.layerName}>{layer.name}</div>
                        <div className={styles.layerMeta}>
                          <span className={styles.layerCount}>{layer.count}개</span>
                          {layer.sampleArea > 0 && (
                            <span className={styles.layerArea}>{layer.sampleArea.toFixed(1)}m²</span>
                          )}
                          {layer.cctvBlocks && (
                            <span
                              className={styles.unsupportedWarn}
                              title={`이 레이어에 CCTV 계열 블록이 있습니다. 레이어 타입대로 선택하면 해당 CCTV 가 다른 타입으로 분류됩니다: ${Object.entries(
                                layer.cctvBlocks
                              )
                                .map(([b, c]) => `${b} ${c}개`)
                                .join(', ')}`}
                            >
                              ⚠ CCTV 블록 {Object.values(layer.cctvBlocks).reduce((a, b) => a + b, 0)}개
                            </span>
                          )}
                          {layer.unsupportedTypes && (
                            <span
                              className={styles.unsupportedWarn}
                              title={`변환 파이프라인이 지원하지 않는 엔티티가 포함돼 있어 해당 도형은 CSV 로 나오지 않습니다: ${Object.entries(
                                layer.unsupportedTypes
                              )
                                .map(([t, c]) => `${t} ${c}개`)
                                .join(', ')}`}
                            >
                              ⚠ 변환불가 {Object.values(layer.unsupportedTypes).reduce((a, b) => a + b, 0)}개
                            </span>
                          )}
                          {showOverrideHint && (
                            <span className={styles.overrideHint} title={`분석 타입: ${analysis}`}>
                              분석: {getTypeDisplayName(analysis)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div
                      className={styles.typeCell}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      <select
                        className={styles.rowTypeSelect}
                        style={{ borderLeftColor: getTypeColor(effective) }}
                        aria-label={`${layer.name} 표준 타입`}
                        value={effective}
                        onChange={(e) => handleRowTypeChange(layer.name, e.target.value)}
                      >
                        {selectOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.labelKo} ({opt.value})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          {siteProjectId != null && (
            <div
              id="dxf-recovery-panel"
              role="tabpanel"
              aria-labelledby="dxf-recovery-tab"
              hidden={activeTab !== 'recovery'}
              className={styles.recoveryPanel}
            >
              {recoveryOpened && (
                <ParkingRecoveryPanel
                  key={`${siteProjectId}/${sourceFilename || filename}`}
                  siteId={siteProjectId}
                  filename={sourceFilename || filename}
                  sourceNames={layers.map((layer) => layer.name)}
                  selectedNames={Array.from(selectedLayers)}
                  onSelectionChange={setRecovery}
                />
              )}
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <div className={styles.importSummary} role="status">
            레이어 {selectedLayers.size}개 · 복원 주차면 {recoveryCount}개 선택
          </div>
          <button type="button" onClick={onCancel} className={styles.cancelButton}>
            취소
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className={styles.confirmButton}
            disabled={selectedLayers.size === 0 && recoveryCount === 0}
          >
            선택한 객체 함께 가져오기
          </button>
        </div>
      </div>
    </div>
  )
}
